import { Badge, Card, Form } from 'react-bootstrap'

// Same accents as the capture-time setup picker, so "Setup 1" looks the same everywhere.
const ACCENTS = ['primary', 'success', 'info', 'warning', 'danger']

const CAMERA_STATUS = {
    ONLINE: { bg: 'success', text: 'Online' },
    NO_IMAGE: { bg: 'warning', text: 'Online, no picture' },
    OFFLINE: { bg: 'danger', text: 'Offline' },
    NOT_CONFIGURED: { bg: 'secondary', text: 'Not set up' },
}

const WB_STATUS = {
    RECEIVING: { bg: 'success', text: 'Receiving' },
    UNREADABLE: { bg: 'warning', text: 'Data not readable' },
    NO_DATA: { bg: 'danger', text: 'No data' },
    PORT_MISSING: { bg: 'danger', text: 'Not connected' },
    NOT_CONFIGURED: { bg: 'secondary', text: 'Not set up' },
}

// Weighbridge readings are in tons, shown with 3 decimals like the capture display.
const formatWeight = (w) => (typeof w === 'number' && !isNaN(w) ? `${w.toFixed(3)} Ton` : '-')

/**
 * One setup group in the Hardware Check: camera status (with a snapshot thumbnail when the
 * camera sent one) and weighbridge status, weight and adapter label. With more than two
 * groups it also offers a "Swap with…" picker.
 */
export function HardwareCheckGroupCard({ group, index, others = [], highlight, disabled, onSwapWith }) {
    const accent = ACCENTS[index % ACCENTS.length]
    const cam = group.camera || {}
    const wb = group.weighbridge || {}
    const camStatus = CAMERA_STATUS[cam.status] || { bg: 'secondary', text: 'Unknown' }
    const wbStatus = WB_STATUS[group.wbStatus] || { bg: 'secondary', text: 'Unknown' }

    return (
        <Card className={`h-100 border border-2 ${highlight ? 'border-danger' : `border-${accent}`}`}>
            <Card.Header className="min-h-50px px-5 d-flex align-items-center">
                <span className={`badge badge-circle bg-${accent} text-white me-3`}>{index + 1}</span>
                <span className="fs-4 fw-bold text-gray-900">{group.name || `Setup ${index + 1}`}</span>
            </Card.Header>
            <Card.Body className="px-5 py-4">
                {/* Camera */}
                <div className="d-flex align-items-center mb-5">
                    {cam.snapshot ? (
                        <img src={`data:image/jpeg;base64,${cam.snapshot}`} alt="Camera snapshot"
                            className="rounded me-4" style={{ width: 120, height: 68, objectFit: 'cover' }} />
                    ) : (
                        <div className="rounded bg-light d-flex align-items-center justify-content-center me-4"
                            style={{ width: 120, height: 68 }}>
                            <i className="bi bi-camera-video-off fs-1 text-muted" />
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="text-muted fs-7">Camera</div>
                        <Badge bg={camStatus.bg}>{camStatus.text}</Badge>
                        {cam.status !== 'ONLINE' && cam.message && <div className="text-muted fs-8 mt-1">{cam.message}</div>}
                    </div>
                </div>

                {/* Weighbridge */}
                <div className="text-muted fs-7">Weighbridge</div>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <Badge bg={wbStatus.bg}>{wbStatus.text}</Badge>
                    <span className="fs-2 fw-bold font-monospace text-gray-900">{formatWeight(group.weight)}</span>
                </div>
                <div className="mt-2 fs-7 text-gray-700">
                    <i className="bi bi-usb-drive me-1" />
                    {wb.adapterLabel || wb.port || 'No port set'}
                    {wb.port && wb.adapterLabel && wb.adapterLabel !== wb.port && (
                        <span className="text-muted ms-2 fs-8">({wb.port})</span>
                    )}
                </div>
                {wb.portCorrectedFrom && (
                    <div className="text-info fs-8 mt-1">
                        <i className="bi bi-magic me-1" />
                        Cable moved from {wb.portCorrectedFrom} — fixed automatically.
                    </div>
                )}
                {wb.error && <div className="text-danger fs-8 mt-1">{wb.error}</div>}

                {others.length > 1 && (
                    <Form.Select size="sm" className="mt-4" value="" disabled={disabled}
                        onChange={(e) => e.target.value && onSwapWith(e.target.value)}>
                        <option value="">Swap weighbridge with…</option>
                        {others.map((o) => (
                            <option key={o.groupId} value={o.groupId}>{o.name}</option>
                        ))}
                    </Form.Select>
                )}
            </Card.Body>
        </Card>
    )
}
